import { autocompleteClasses } from '@mui/material'
import grey from '@mui/material/colors/grey'
import { inputLabelClasses } from '@mui/material/InputLabel'
import { outlinedInputClasses } from '@mui/material/OutlinedInput'
import { chipClasses } from '@mui/material/Chip'
import { createTheme } from '@mui/material/styles'

// Augment the palette to include an ochre color
declare module '@mui/material/styles' {
  interface Palette {
    green: Palette['primary']
  }

  interface PaletteOptions {
    green?: PaletteOptions['primary']
  }
}

// Update the Button's color options to include an ochre option
declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    green: true
  }
}

const createShadow = (pv, pb, ps, uv, ub, us, av, ab, as) =>
  [
    `0 ${pv}px ${pb}px ${ps}px rgba(0, 0, 0, 0.2)`,
    `0 ${uv}px ${ub}px ${us}px rgba(0, 0, 0, 0.14)`,
    `0 ${av}px ${ab}px ${as}px rgba(0, 0, 0, 0.12)`,
  ].join(',')

export const ICON_BUTTON_SIZE = 48

const fontFamily = ['Jost', 'Roboto', 'Helvetica Nue', 'sans-serif'].join(',')

export default colors =>
  createTheme({
    palette: {
      green: {
        main: '#00BE91',
        light: '#00BE91',
        dark: '#00BE91',
        contrastText: '#ffffff',
      },
      action: {
        active: colors.checkbox.default,
      },
      background: colors.background,
      divider: colors.divider,
      error: {
        main: colors.error,
      },
      primary: {
        contrastText: '#ffffff',
        dark: colors.font.textDisabled,
        main: colors.primary,
      },
      secondary: {
        contrastText: '#ffffff',
        main: colors.secondary,
      },
      text: {
        disabled: colors.font.gray,
        primary: colors.font.default,
        secondary: colors.font.gray,
      },
      mode: colors.theme,
    },
    components: {
      MuiSvgIcon: {
        styleOverrides: {
          fontSizeLarge: {
            fontSize: '2.4rem',
          },
          root: {
            fontSize: '1.8rem',
          },
        },
      },
      MuiButton: {
        defaultProps: {
          color: 'primary',
          disableElevation: true,
          disableFocusRipple: true,
        },
        styleOverrides: {
          root: {
            fontWeight: 500,
            textTransform: 'none',
            // borderRadius: 0,
            lineHeight: 1.4,
            '&.transparent:hover': {
              backgroundColor: 'transparent',
            },
            '&.no-padding': {
              padding: 0,
            },
          },
          sizeSmall: {
            padding: '4px 8px',
          },
          sizeMedium: {
            padding: '8px 16px',
            fontSize: '14px',
          },
          sizeLarge: {
            padding: '12px 24px',
            fontSize: '17px',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          sizeMedium: {
            height: 36,
            fontSize: '13px',
          },
          label: {
            paddingLeft: '5px',
            paddingRight: '5px',
          },
          deleteIconMedium: {
            fontSize: '16px',
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            fontSize: '16px',
            textTransform: 'initial',
            fontWeight: 400,
            '&.Mui-selected': {
              fontWeight: 700,
            },
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          input: {
            fontSize: 12,
            fontWeight: 700,
          },
        },
      },
      MuiRating: {
        styleOverrides: {
          root: {
            color: grey[900],
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            color: 'inherit',
            padding: 3,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          // root: {
          //   borderRadius: 0,
          // },
          input: {
            paddingTop: '15px',
            paddingBottom: '15px',
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            transform: 'translate(14px, 12px) scale(1)',
            [`&.${inputLabelClasses.shrink}`]: {
              transform: 'translate(14px, -7px) scale(0.75)',
            },
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            fontSize: '1.3rem',
            zIndex: 1,
            color: '#666',
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          root: {
            [`& .${outlinedInputClasses.root}`]: {
              padding: 0,
              [`& .${autocompleteClasses.input}`]: {
                padding: '15px 6px 15px 14px',
              },
            },
            [`& .${chipClasses.label}`]: {
              paddingLeft: '12px',
              paddingRight: '12px',
            },
          },
          inputRoot: {
            padding: 0,
          },
          // endAdornment: {
          //   top: 'calc(50% - 11px)',
          // },
          tag: {
            height: '28px',
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            textAlign: 'center',
            padding: '12.5px 24px',
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            padding: '14px 14px',
          },
          icon: {
            top: 'calc(50% - 10px)',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          root: {
            zIndex: 1300,
          },
          paper: {
            maxWidth: '100%',
          },
        },
      },

      MuiFormControl: {
        defaultProps: {
          variant: 'outlined',
        },
      },
      MuiTextField: {
        defaultProps: {
          variant: 'outlined',
          margin: 'normal',
          fullWidth: true,
          size: 'medium',
        },
      },
      MuiLink: {
        defaultProps: {
          underline: 'always',
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: '#f2f2f2',
            },
          },
        },
      },
    },
    shadows: [
      'none',
      createShadow(1, 1, 0, 2, 1, -2, 1, 3, 0),
      createShadow(2, 2, 0, 3, 1, -2, 1, 5, 0),
      createShadow(3, 4, 0, 3, 3, -2, 1, 8, 0),
      createShadow(4, 5, 0, 1, 10, 0, 2, 4, -1),
      createShadow(5, 8, 0, 1, 14, 0, 3, 4, -1),
      createShadow(6, 10, 0, 1, 18, 0, 3, 5, -1),
      createShadow(7, 10, 0, 2, 16, 1, 4, 5, -2),
      createShadow(8, 10, 1, 3, 14, 2, 5, 5, -3),
      createShadow(9, 12, 1, 3, 16, 3, 5, 6, -4),
      createShadow(10, 14, 1, 4, 18, 3, 6, 7, -4),
      createShadow(11, 16, 1, 4, 20, 3, 6, 7, -4),
      createShadow(12, 17, 1, 5, 22, 4, 7, 8, -4),
      createShadow(13, 19, 1, 5, 24, 4, 7, 8, -4),
      createShadow(14, 21, 1, 5, 26, 4, 7, 9, -5),
      createShadow(15, 22, 1, 5, 28, 4, 7, 9, -5),
      createShadow(16, 24, 2, 6, 30, 5, 8, 10, -5),
      createShadow(15, 27, 3, 7, 28, 3, 10, 14, -4),
      createShadow(14, 30, 4, 8, 26, 1, 12, 17, -3),
      createShadow(13, 33, 4, 8, 24, -1, 14, 20, -1),
      createShadow(12, 36, 5, 9, 22, -2, 16, 24, 1),
      createShadow(11, 39, 6, 10, 20, -4, 18, 28, 1),
      createShadow(10, 41, 7, 10, 18, -5, 20, 31, 2),
      createShadow(9, 44, 7, 11, 16, -6, 22, 35, 2),
      createShadow(9, 46, 8, 11, 15, -7, 24, 38, 3),
    ],
    typography: {
      htmlFontSize: 10,
      fontSize: 12,
      fontWeightLight: 300,
      fontWeightRegular: 400,
      fontWeightMedium: 600,
      fontFamily,
      allVariants: {
        fontFamily,
      },
      body1: {
        fontSize: '1.4rem',
      },
      body2: {
        fontSize: '1.2rem',
      },
      subtitle1: {
        fontSize: '14px',
        fontWeight: 600,
        textTransform: 'uppercase',
      },
      h3: {
        fontSize: '28px',
      },
      h4: {
        fontSize: '22px',
      },
      h5: {
        fontSize: '18px',
      },
      h6: {
        fontSize: '16px',
      },
    },
  })
